/**
 * CL-DRAW-ORDER-001 / 001A — Explicit drawing document role (overall package vs individual).
 *
 * Role is admin-controlled on pmProjectFiles. Never inferred from filename,
 * displayTitle, upload order, or drawing position.
 *
 * Distinct from the per-collaborator “Prepare review package” share selection.
 *
 * Uniqueness: designation writes all role changes in one Firestore transaction.
 * Concurrent admin sessions that promote two previously-individual files can still
 * produce two overall_package rows (no collection query inside a transaction).
 * Readers must use resolveOverallPackageState() and must not pick an arbitrary
 * “current” package when more than one exists.
 */

import { sortDrawingsCanonical } from "./drawingOrderModel";
import { isStorageReadyForPortal } from "./projectFileStoragePublicationModel";
import { isCurrentDrawingLibraryRecord } from "./drawingOrganizationModel";
import { getEffectiveDrawingTaxonomy } from "./drawingTaxonomyModel";
import {
  projectUsesTaxonomyDrawingOrder,
  sortDrawingsForTaxonomyPresentation,
} from "./drawingTaxonomyOrderModel";
import { isClientPortalIndividualDrawing } from "./drawingPortalIndividualEligibility";

export const DRAWING_DOCUMENT_ROLES = Object.freeze({
  OVERALL_PACKAGE: "overall_package",
  INDIVIDUAL: "individual",
});

export const OVERALL_PACKAGE_SHARE_WARNING =
  "Sharing this combined PDF grants access to every sheet contained in it, regardless of the recipient’s individual drawing assignments.";

export const OVERALL_PACKAGE_CONFLICT_COPY =
  "Multiple files are marked as the overall drawing package. None is treated as current. Choose one file to keep as the package; the others return to individual drawings. Portal viewers will not see a current package until this is resolved.";

function safeStr(value) {
  try {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  } catch {
    return "";
  }
}

function lower(value) {
  return safeStr(value).toLowerCase();
}

function fileIdOf(fileOrId) {
  if (fileOrId && typeof fileOrId === "object") return safeStr(fileOrId.id || fileOrId.fileId);
  return safeStr(fileOrId);
}

function isLiveDrawingFile(file) {
  if (!file) return false;
  if (file.deleted === true) return false;
  return lower(file?.status || "active") !== "deleted";
}

/**
 * Missing/legacy/malformed values default to individual. Filename is never used.
 */
export function normalizeDrawingDocumentRole(value) {
  return lower(value) === DRAWING_DOCUMENT_ROLES.OVERALL_PACKAGE
    ? DRAWING_DOCUMENT_ROLES.OVERALL_PACKAGE
    : DRAWING_DOCUMENT_ROLES.INDIVIDUAL;
}

export function isOverallDrawingPackage(file) {
  return normalizeDrawingDocumentRole(file?.drawingDocumentRole) === DRAWING_DOCUMENT_ROLES.OVERALL_PACKAGE;
}

export function isIndividualDrawing(file) {
  return !isOverallDrawingPackage(file);
}

export function findActiveOverallPackages(files = []) {
  return (Array.isArray(files) ? files : []).filter(
    (f) => f && isLiveDrawingFile(f) && isOverallDrawingPackage(f)
  );
}

/**
 * Safe current-package resolver. Returns null when zero or more than one
 * overall_package file exists. Never arbitrarily chooses among conflicts.
 */
export function resolveOverallPackageState(files = []) {
  const packages = findActiveOverallPackages(files);
  const conflictingPackages = [...packages].sort((a, b) =>
    fileIdOf(a).localeCompare(fileIdOf(b))
  );
  if (packages.length === 1) {
    return {
      overallPackage: packages[0],
      packageConflict: false,
      conflictingPackages: [],
    };
  }
  return {
    overallPackage: null,
    packageConflict: packages.length > 1,
    conflictingPackages,
  };
}

/** Current package only when uniqueness holds. Null on conflict. */
export function findActiveOverallPackage(files = []) {
  return resolveOverallPackageState(files).overallPackage;
}

/**
 * Pure uniqueness plan. At most one overall_package among the given files.
 * Does not write Firestore.
 */
export function buildOverallPackageDesignationPatches(files = [], nextPackageFileId = "") {
  const targetId = fileIdOf(nextPackageFileId);
  const patches = [];
  const seen = new Set();
  (Array.isArray(files) ? files : []).forEach((f) => {
    const id = fileIdOf(f);
    if (!id || seen.has(id)) return;
    seen.add(id);
    const nextRole =
      id === targetId ? DRAWING_DOCUMENT_ROLES.OVERALL_PACKAGE : DRAWING_DOCUMENT_ROLES.INDIVIDUAL;
    const prevRole = normalizeDrawingDocumentRole(f?.drawingDocumentRole);
    if (prevRole === nextRole && id !== targetId) return;
    if (id === targetId || prevRole === DRAWING_DOCUMENT_ROLES.OVERALL_PACKAGE) {
      patches.push({
        fileId: id,
        drawingDocumentRole: nextRole,
      });
    }
  });
  if (targetId && !patches.some((p) => p.fileId === targetId)) {
    patches.push({ fileId: targetId, drawingDocumentRole: DRAWING_DOCUMENT_ROLES.OVERALL_PACKAGE });
  }
  return patches;
}

export function overallPackageDesignationRequiresConfirm(files = [], nextPackageFileId = "") {
  const targetId = fileIdOf(nextPackageFileId);
  return findActiveOverallPackages(files).some((f) => fileIdOf(f) !== targetId);
}

export function collectOverallPackageTransactionFileIds({
  knownFiles = [],
  targetFileId = "",
} = {}) {
  const ids = [];
  const seen = new Set();
  const add = (id) => {
    const v = fileIdOf(id);
    if (!v || seen.has(v)) return;
    seen.add(v);
    ids.push(v);
  };
  add(targetFileId);
  (Array.isArray(knownFiles) ? knownFiles : []).forEach((f) => {
    if (isOverallDrawingPackage(f)) add(f);
  });
  return ids;
}

/**
 * Plan role writes from freshly read snapshots. Promote sets exactly one
 * overall_package among the snapshotted files; retire touches only the target.
 */
export function planOverallPackageWritesFromSnapshots({
  snapshots = [],
  targetFileId = "",
  retire = false,
} = {}) {
  const targetId = fileIdOf(targetFileId);
  const writes = [];
  const seen = new Set();
  (Array.isArray(snapshots) ? snapshots : []).forEach((row) => {
    const id = fileIdOf(row?.fileId || row?.id);
    if (!id || seen.has(id) || row?.exists === false) return;
    seen.add(id);
    const prevRole = normalizeDrawingDocumentRole(row?.data?.drawingDocumentRole);
    if (retire) {
      if (id === targetId) {
        writes.push({ fileId: id, drawingDocumentRole: DRAWING_DOCUMENT_ROLES.INDIVIDUAL });
      }
      return;
    }
    const nextRole =
      id === targetId ? DRAWING_DOCUMENT_ROLES.OVERALL_PACKAGE : DRAWING_DOCUMENT_ROLES.INDIVIDUAL;
    if (id === targetId || prevRole === DRAWING_DOCUMENT_ROLES.OVERALL_PACKAGE) {
      writes.push({ fileId: id, drawingDocumentRole: nextRole });
    }
  });
  if (!retire && targetId && !writes.some((w) => w.fileId === targetId)) {
    writes.push({ fileId: targetId, drawingDocumentRole: DRAWING_DOCUMENT_ROLES.OVERALL_PACKAGE });
  }
  return writes;
}

/**
 * Atomic designation writer. All candidate docs are read, then all role writes
 * (plus optional extra field patches) commit in the same transaction.
 * A failed transaction changes nothing.
 */
export async function commitOverallPackageDesignation({
  db,
  runTransaction,
  doc,
  serverTimestamp,
  knownFiles = [],
  targetFileId = "",
  retire = false,
  extraUpdatesByFileId = {},
} = {}) {
  if (typeof runTransaction !== "function" || typeof doc !== "function") {
    throw new Error("commitOverallPackageDesignation requires Firestore runTransaction and doc");
  }
  const targetId = fileIdOf(targetFileId);
  if (!targetId) {
    throw new Error("commitOverallPackageDesignation requires a target file id");
  }
  const candidateIds = collectOverallPackageTransactionFileIds({
    knownFiles,
    targetFileId: targetId,
  });
  await runTransaction(db, async (tx) => {
    const snapshots = [];
    for (let i = 0; i < candidateIds.length; i += 1) {
      const fileId = candidateIds[i];
      const ref = doc(db, "pmProjectFiles", fileId);
      const snap = await tx.get(ref);
      snapshots.push({
        fileId,
        exists: snap.exists(),
        data: snap.exists() ? snap.data() : null,
      });
    }
    const writes = planOverallPackageWritesFromSnapshots({
      snapshots,
      targetFileId: targetId,
      retire,
    });
    const stamp = typeof serverTimestamp === "function" ? serverTimestamp() : null;
    const extraMap = extraUpdatesByFileId && typeof extraUpdatesByFileId === "object" ? extraUpdatesByFileId : {};
    const written = new Set();
    writes.forEach((w) => {
      const extra = extraMap[w.fileId] && typeof extraMap[w.fileId] === "object" ? extraMap[w.fileId] : {};
      const payload = {
        ...extra,
        drawingDocumentRole: w.drawingDocumentRole,
      };
      if (stamp) payload.updatedAt = stamp;
      tx.update(doc(db, "pmProjectFiles", w.fileId), payload);
      written.add(w.fileId);
    });
    Object.keys(extraMap).forEach((id) => {
      const fileId = fileIdOf(id);
      if (!fileId || written.has(fileId)) return;
      const payload = { ...extraMap[fileId] };
      if (stamp) payload.updatedAt = stamp;
      tx.update(doc(db, "pmProjectFiles", fileId), payload);
    });
  });
}

/**
 * Preserve relative individual order; drop overall-package files from the ordered list.
 */
export function individualDrawingsInAdminOrder(files = [], projectOrConfig = null) {
  const list = (Array.isArray(files) ? files : []).filter((f) => f && isIndividualDrawing(f));
  if (projectUsesTaxonomyDrawingOrder(projectOrConfig, list)) {
    const taxonomy = getEffectiveDrawingTaxonomy(projectOrConfig, list);
    return sortDrawingsForTaxonomyPresentation(list, taxonomy, {
      project: projectOrConfig,
      includeExcluded: true,
    });
  }
  return sortDrawingsCanonical(list, projectOrConfig, { includeExcluded: true });
}

export function pinnedOverallPackageIds(files = []) {
  return findActiveOverallPackages(files).map((f) => fileIdOf(f)).filter(Boolean);
}

/**
 * Authorized portal presentation: package (if entitled and unique) + one individual list.
 * Sequence: canonical admin order → entitlement already applied on `authorizedFiles`
 * → exclude package role files → preserve remaining relative order.
 * Conflict: do not expose a current package.
 */
export function buildPortalDrawingsPresentation({
  authorizedFiles = [],
  project = null,
} = {}) {
  const entitled = (Array.isArray(authorizedFiles) ? authorizedFiles : []).filter(
    (f) =>
      f &&
      isLiveDrawingFile(f) &&
      isCurrentDrawingLibraryRecord(f) &&
      isStorageReadyForPortal(f)
  );
  const state = resolveOverallPackageState(entitled);
  const presentableIndividuals = entitled.filter((f) => isClientPortalIndividualDrawing(f));
  const individualDrawings = individualDrawingsInAdminOrder(presentableIndividuals, project);
  return {
    overallPackage: state.overallPackage,
    individualDrawings,
    individualCount: individualDrawings.length,
    hasEntitledPackage: Boolean(state.overallPackage),
    packageConflict: state.packageConflict,
    conflictingPackages: state.conflictingPackages,
  };
}

export function countIndividualDrawings(files = []) {
  return (Array.isArray(files) ? files : []).filter((f) => f && isIndividualDrawing(f)).length;
}

/** Canonical portal-presentable current individual count (authorized files in, same as ClientProjectDetail). */
export function portalCurrentIndividualDrawingCount(authorizedFiles = [], project = null) {
  return buildPortalDrawingsPresentation({ authorizedFiles, project }).individualCount;
}

export function portalDrawingsDashboardPresentation(authorizedFiles = [], project = null) {
  const presentation = buildPortalDrawingsPresentation({ authorizedFiles, project });
  return {
    individualCount: presentation.individualCount,
    hasOverallPackage: presentation.hasEntitledPackage,
    individualDrawings: presentation.individualDrawings,
  };
}

export function formatDashboardDrawingCountLabel(individualCount, { hasOverallPackage = false } = {}) {
  const n = Math.max(0, Number(individualCount) || 0);
  const currentDrawings =
    n === 0
      ? "No current individual drawings"
      : n === 1
        ? "1 current drawing"
        : `${n} current drawings`;
  if (hasOverallPackage) return `Overall package available · ${currentDrawings}`;
  if (n === 0) return "No drawings shared with you yet.";
  if (n === 1) return "1 drawing shared with you.";
  return `${n} drawings shared with you.`;
}

/**
 * When saving Order drawings, keep any previously stored package id at its old
 * index so returning it to individual restores the prior slot.
 */
export function mergePinnedIdsIntoSavedOrder({
  previousOrder = [],
  individualOrder = [],
  pinnedIds = [],
} = {}) {
  const pinned = new Set((Array.isArray(pinnedIds) ? pinnedIds : []).map(fileIdOf).filter(Boolean));
  const individuals = [];
  const seenInd = new Set();
  (Array.isArray(individualOrder) ? individualOrder : []).forEach((id) => {
    const v = fileIdOf(id);
    if (!v || pinned.has(v) || seenInd.has(v)) return;
    seenInd.add(v);
    individuals.push(v);
  });
  const prev = [];
  const seenPrev = new Set();
  (Array.isArray(previousOrder) ? previousOrder : []).forEach((id) => {
    const v = fileIdOf(id);
    if (!v || seenPrev.has(v)) return;
    seenPrev.add(v);
    prev.push(v);
  });

  if (!pinned.size) return individuals;

  const next = [];
  let i = 0;
  prev.forEach((id) => {
    if (pinned.has(id)) {
      next.push(id);
      return;
    }
    if (i < individuals.length) {
      next.push(individuals[i]);
      i += 1;
    }
  });
  while (i < individuals.length) {
    next.push(individuals[i]);
    i += 1;
  }
  pinned.forEach((id) => {
    if (!next.includes(id)) next.push(id);
  });
  return next;
}
